import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '@/utils/translations';
import { cn } from '@/lib/utils';
import NavLink from './NavLink';
import {
  handleHomeNavigation,
  handleServicesNavigation,
  handleAboutNavigation
} from '@/utils/navigation';
import { localizePath, stripLanguagePrefix } from '@/utils/languageRoutes';

interface NavLinksProps {
  isActive: (section: string) => boolean;
  isMobile?: boolean;
  onMobileClick?: () => void;
  language: string;
  textColor?: string;
}

const NavLinks = ({ 
  isActive, 
  isMobile = false, 
  onMobileClick, 
  language, 
  textColor = 'text-gray-600' 
}: NavLinksProps) => {
  const t = useTranslation(language);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = stripLanguagePrefix(location.pathname);

  const linkClass = isMobile
    ? cn(
        "block px-3 py-2 text-gray-600 hover:text-primary relative",
        "after:content-[''] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:bg-primary after:scale-x-0 after:transition-transform after:duration-200",
        "hover:after:scale-x-100"
      )
    : textColor;

  return (
    <>
      <NavLink
        to={localizePath('/', language)}
        onClick={(e) => handleHomeNavigation(e, navigate, location.pathname, language, onMobileClick)}
        isActive={isActive('home')}
        className={linkClass}
      >
        {t.navbar.home}
      </NavLink>

      <NavLink
        to={`${localizePath('/', language)}#services`}
        onClick={(e) => handleServicesNavigation(e, navigate, location.pathname, language, onMobileClick)}
        isActive={isActive('services')}
        className={linkClass}
      >
        {t.navbar.services}
      </NavLink>

      <NavLink
        to={localizePath('/about', language)}
        onClick={(e) => handleAboutNavigation(e, navigate, language, onMobileClick)}
        isActive={isActive('about') || currentPath === '/about'}
        className={linkClass}
      >
        {t.navbar.about}
      </NavLink>

      <NavLink
        to={localizePath('/gallery', language)}
        onClick={onMobileClick}
        isActive={currentPath === '/gallery'}
        className={linkClass}
      >
        {t.navbar.gallery}
      </NavLink>

      <NavLink
        to={localizePath('/insights', language)}
        onClick={onMobileClick}
        isActive={isActive('insights') || currentPath === '/insights'}
        className={linkClass}
      >
        {t.navbar.insights}
      </NavLink>
    </>
  );
};

export default NavLinks;
